import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bell, RefreshCw, MonitorSmartphone } from "lucide-react";

/**
 * Auto-detect the current browser/OS so we can show step-by-step
 * instructions to unblock browser notifications.
 * Falls back to a generic guide when we can't decide.
 */
function detectBrowser() {
  if (typeof navigator === "undefined") return "generic";
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge && !isFirefox;
  if (isIOS) return "ios-safari";
  if (isAndroid && isChrome) return "android-chrome";
  if (isAndroid && isFirefox) return "android-firefox";
  if (isFirefox) return "firefox";
  if (isSafari) return "safari";
  if (isEdge || isChrome) return "chrome";
  return "generic";
}

const GUIDES = {
  chrome: {
    label: "Chrome / Edge (desktop)",
    steps: [
      "Nella barra degli indirizzi, clicca l'icona 🔒 (lucchetto) o ⚙️ a sinistra dell'URL.",
      "Trova la voce Notifiche e cambia da «Blocca» a «Consenti».",
      "Ricarica la pagina (F5).",
      "Torna qui e clicca di nuovo su «Attiva notifiche».",
    ],
  },
  firefox: {
    label: "Firefox (desktop)",
    steps: [
      "Clicca il lucchetto 🔒 accanto all'URL.",
      "Clicca «Autorizzazioni» → alla voce «Invia notifiche» rimuovi il blocco (X sulla riga).",
      "Ricarica la pagina.",
      "Torna qui e clicca di nuovo su «Attiva notifiche».",
    ],
  },
  safari: {
    label: "Safari (macOS)",
    steps: [
      "Menu Safari → Impostazioni → tab Siti web → sezione Notifiche.",
      "Trova questo sito nell'elenco e cambialo in «Consenti».",
      "Ricarica la pagina.",
      "Torna qui e clicca di nuovo su «Attiva notifiche».",
    ],
  },
  "android-chrome": {
    label: "Chrome Android",
    steps: [
      "Tocca i ⋮ (tre puntini) in alto a destra → «Info sito».",
      "Autorizzazioni → Notifiche → «Consenti».",
      "Torna qui e ricarica la pagina.",
      "Tocca di nuovo «Attiva notifiche».",
    ],
  },
  "android-firefox": {
    label: "Firefox Android",
    steps: [
      "Tocca i ⋮ (tre puntini) → «Impostazioni» → «Autorizzazioni siti».",
      "Notifiche → trova questo sito → «Consenti».",
      "Torna qui e ricarica la pagina.",
      "Tocca di nuovo «Attiva notifiche».",
    ],
  },
  "ios-safari": {
    label: "iPhone / iPad (Safari)",
    steps: [
      "Le notifiche web su iOS funzionano solo se il sito è aggiunto alla schermata Home come PWA.",
      "In Safari tocca l'icona Condividi → «Aggiungi a schermata Home».",
      "Apri l'app dall'icona appena creata sulla Home.",
      "Vai in Area riservata e tocca «Attiva notifiche»: iOS chiederà il permesso.",
    ],
    note: "Richiede iOS 16.4 o successivo.",
  },
  generic: {
    label: "Altro browser",
    steps: [
      "Apri le impostazioni del sito nel tuo browser (di solito icona lucchetto o info accanto all'URL).",
      "Cerca la voce «Notifiche» e cambiala in «Consenti».",
      "Ricarica la pagina.",
      "Torna qui e clicca di nuovo su «Attiva notifiche».",
    ],
  },
};

export default function NotificationsHelpDialog({ open, onOpenChange }) {
  const guide = useMemo(() => {
    const key = detectBrowser();
    return { key, ...GUIDES[key] };
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notifications-help-dialog" className="max-w-lg p-4 sm:p-6 gap-3 sm:gap-4 max-h-[92vh] overflow-y-auto">
        <DialogHeader className="pr-8">
          <DialogTitle className="font-head font-black text-xl sm:text-2xl tracking-tightest flex items-center gap-2">
            <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" /> Sblocca le notifiche
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Il browser ha bloccato le notifiche per questo sito. Segui i passaggi per il tuo browser per riattivarle.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-border p-4 bg-secondary/40">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-2">
            <MonitorSmartphone className="w-3.5 h-3.5" /> Browser rilevato
          </p>
          <p className="font-head font-bold text-base tracking-tight">{guide.label}</p>
        </div>

        <ol className="space-y-3">
          {guide.steps.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 grid place-items-center bg-primary text-primary-foreground font-mono text-xs font-bold">
                {i + 1}
              </span>
              <span className="text-sm leading-relaxed pt-0.5">{s}</span>
            </li>
          ))}
        </ol>

        {guide.note && (
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-l-2 border-primary pl-3">
            {guide.note}
          </p>
        )}

        <div className="pt-3 border-t border-border flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="notifications-help-reload"
            className="flex-1 inline-flex items-center justify-center gap-2 border border-border p-3 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="font-head font-bold text-sm tracking-tight">Ricarica la pagina</span>
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="notifications-help-close"
            className="flex-1 inline-flex items-center justify-center gap-2 border border-border p-3 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer bg-transparent"
          >
            <span className="font-head font-bold text-sm tracking-tight">Ho capito</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
